import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  AssetColorLabel,
  AssetDto,
  AssetStatus,
  GalleryColorLabel,
  GalleryDto,
  GalleryFeedbackOptions,
} from '@picflow/types';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AssetStatusButtonComponent } from '../../../gallery/components/asset-status-button/asset-status-button.component';
import { ColorLabelContextComponent } from '../../../gallery/components/color-label-context/color-label-context.component';
import { GallerySettingsDialogComponent } from '../../../gallery/dialogs/gallery-settings-dialog/gallery-settings-dialog.component';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { sleep } from '../../../lib/sleep';
import { AbsPipe } from '../../../pipes/abs.pipe';
import { AssetApprovalLimitService } from '../../../services/asset-approval-limit.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SmoothCounterComponent } from '../../../shared/components/smooth-counter/smooth-counter.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslateLabelPipe } from '../../../shared/pipes/translate-label.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { feedbackButtonSizes } from './fits';

@Component({
  selector: 'app-asset-page-footer',
  templateUrl: './asset-page-footer.component.html',
  styleUrls: ['./asset-page-footer.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    ContextMenuComponent,
    ButtonMenuItemComponent,
    TranslatePipe,
    TranslateLabelPipe,
    SmoothCounterComponent,
    AbsPipe,
    AssetStatusButtonComponent,
    ColorLabelContextComponent,
  ],
})
export class AssetPageFooterComponent implements OnChanges, OnDestroy, OnInit {
  private dialog = inject(DialogService);
  private galleryService = inject(GalleryService);
  private assetService = inject(AssetService);
  private assetApprovalLimitService = inject(AssetApprovalLimitService);
  private check = inject(ChangeDetectorRef);
  private permissionService = inject(PermissionService);
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() sideMenuOpen: boolean;
  @Input() currentLabel: GalleryColorLabel;
  @Input() colorLabels: GalleryColorLabel[] = [];
  @Input() accessLevel: GalleryAccessLevel;
  @Input() commentPlacementMode: boolean;

  @Output() onToggleLike = new EventEmitter();
  @Output() onUpdateAssetStatus = new EventEmitter();
  @Output() onUpdateColorLabel = new EventEmitter();
  @Output() toggleCommentPlacementMode = new EventEmitter();
  @Output() onStartAnnotation = new EventEmitter();

  @HostListener('window:resize', ['$event']) winResize() {
    if (!this.asset) return;
    this.calc();
  }

  display: { [propName: string]: boolean };
  allowedAndEnabledFeedbackOptions: (GalleryFeedbackOptions | 'display-status')[];
  approvedAssetCount$: Observable<number>;
  destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.galleryService.onGalleryUpdated$.pipe(takeUntil(this.destroyed$)).subscribe(async gallery => {
      if (!gallery || !this.gallery) return;
      if (gallery.id !== this.gallery.id) return;
      await sleep(0);
      this.calc();
    });

    this.assetService.updates.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.check.detectChanges();
    });

    if (this.gallery) {
      this.approvedAssetCount$ = this.assetApprovalLimitService.getApprovedAssetCountForGallery(this.gallery.id);
    }
    this.calc();
  }

  ngOnChanges() {
    this.calc();
  }

  private showApprovedBadgeOnly() {
    if (this.asset.status !== 'approved') return false;
    if (!this.gallery.download_single_asset) return false;
    return true;
  }

  calc() {
    this.display = {};
    this.allowedAndEnabledFeedbackOptions = this.permissionService.getAllowedAndEnabledFeedbackOptions(
      this.gallery,
      this.accessLevel
    ) as (GalleryFeedbackOptions | 'display-status')[];

    if (!this.allowedAndEnabledFeedbackOptions.includes('status') && this.showApprovedBadgeOnly()) {
      this.allowedAndEnabledFeedbackOptions.push('display-status');
    }

    this.allowedAndEnabledFeedbackOptions.forEach(option => {
      if (!feedbackButtonSizes[option]) return;
      this.display[option] = true;
    });

    this.check.detectChanges();
  }

  toggleLike() {
    this.onToggleLike.emit();
  }

  async updateAssetStatus(status: AssetStatus) {
    this.onUpdateAssetStatus.emit(status);
  }

  updateColorLabel(label: AssetColorLabel) {
    this.onUpdateColorLabel.emit(label);
  }

  openReviewSettings() {
    this.dialog.open(GallerySettingsDialogComponent, { gallery: this.gallery, defaultMode: 'review' });
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
